import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Plus, Edit2, Trash2, Save, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { value: 'general', label: 'Règles Générales' },
  { value: 'roleplay', label: 'Règles Roleplay' },
  { value: 'comportement', label: 'Comportement' },
  { value: 'sanctions', label: 'Sanctions' }
];

const AdminRules = () => {
  const { token } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    order: 0
  });

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/rules`);
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      order: rules.length
    });
    setEditingRule(null);
  };

  const openDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        title: rule.title,
        content: rule.content,
        category: rule.category || 'general',
        order: rule.order || 0
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRule) {
        await axios.put(`${API}/admin/rules/${editingRule.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Règle modifiée avec succès !');
      } else {
        await axios.post(`${API}/admin/rules`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Règle ajoutée avec succès !');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette règle ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Règle supprimée !');
      fetchRules();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  // Group rules by category
  const groupedRules = rules.reduce((acc, rule) => {
    const category = rule.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(rule);
    return acc;
  }, {});

  if (loading) {
    return <Loader text="Chargement des règles..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-violet-400" />
            Gestion du Règlement
          </h1>
          <p className="text-gray-400">{rules.length} règle(s)</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="add-rule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider">
                {editingRule ? 'Modifier la règle' : 'Ajouter une règle'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Titre de la règle"
                  required
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="rule-title-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Catégorie</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1" data-testid="rule-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-gray-300">Contenu</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Détails de la règle..."
                  required
                  rows={6}
                  className="bg-black/50 border-white/10 text-white mt-1 resize-none"
                  data-testid="rule-content-input"
                />
              </div>
              
              <div>
                <Label className="text-gray-300">Ordre d'affichage</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="rule-order-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-violet-600 hover:bg-violet-500"
                  data-testid="save-rule-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass rounded-xl p-12 text-center"
        >
          <BookOpen className="w-16 h-16 text-violet-500/50 mx-auto mb-4" />
          <p className="text-gray-400">Aucune règle définie</p>
          <Button 
            onClick={() => openDialog()}
            className="mt-4 bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter la première règle
          </Button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {categories.map((category) => {
            const categoryRules = groupedRules[category.value] || [];
            if (categoryRules.length === 0) return null;
            
            return (
              <motion.div
                key={category.value}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl p-6"
              >
                <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
                  {category.label}
                </h2>
                
                <div className="space-y-3">
                  {categoryRules.map((rule, index) => (
                    <div
                      key={rule.id}
                      className="flex items-start gap-4 p-4 bg-black/30 rounded-lg group"
                      data-testid={`rule-card-${rule.id}`}
                    >
                      <div className="text-gray-500">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium">{rule.title}</h3>
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2">{rule.content}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openDialog(rule)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                          data-testid={`edit-rule-${rule.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(rule.id)}
                          className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          data-testid={`delete-rule-${rule.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminRules;
